import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ChevronDown, CloudOff, LayoutDashboard, MonitorPlay, TriangleAlert } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'

import { MarcaOP } from '@/components/marca'
import { ConviteInstalar } from '@/components/pwa/convite-instalar'
import { VersaoApp } from '@/components/pwa/versao-app'
import { EsqueletoCartoes, EsqueletoLinhas } from '@/components/ui/estado'
import { useRelogio } from '@/hooks/use-relogio'
import { cn } from '@/lib/cn'
import { plural } from '@/lib/formato'
import { prazoVencido } from '@/lib/pedidos'
import { estourou } from '@/lib/postos'
import { lembrarArea } from '@/lib/pwa'
import { PERFIS_WEB, exigirSessao } from '@/lib/sessao'
import { useTRPC } from '@/lib/trpc'

const CLASSE_ATALHO =
  'press flex h-9 items-center gap-1.5 rounded-full border border-border bg-elevated/60 px-3 text-sm font-medium text-fg-muted transition-colors hover:border-brand/60 hover:text-fg'

export const Route = createFileRoute('/diretoria')({
  beforeLoad: async ({ context, location }) => {
    await exigirSessao({ context, location, perfis: PERFIS_WEB })
  },
  component: Diretoria,
})

/**
 * Prazo (coluna DATE, meia-noite UTC) em palavras curtas para o celular:
 * "hoje", "amanhã" ou "23/10".
 */
function quando(prazo: Date | string): string {
  const d = new Date(prazo)
  const hoje = new Date()
  const dias = Math.round(
    (Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) -
      Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())) /
      86_400_000,
  )
  if (dias === 0) return 'hoje'
  if (dias === 1) return 'amanhã'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })
}

/** Quantos itens cada seção mostra antes do "Ver todos". */
const RESUMO = 3

/**
 * Celular do diretor: a fábrica numa tela. Números do dia no topo, o que
 * pede atenção logo abaixo e, em cada lista, só os primeiros itens — o resto
 * abre no "Ver todos", sem transformar a tela numa rolagem sem fim.
 */
function Diretoria() {
  const trpc = useTRPC()
  const relogio = useRelogio()
  useEffect(() => lembrarArea('/diretoria'), [])
  const agora = useQuery({ ...trpc.painel.agora.queryOptions(), refetchInterval: 30_000 })
  const producao = useQuery({
    ...trpc.painel.producaoDoDia.queryOptions(),
    refetchInterval: 60_000,
  })
  const atrasados = useQuery({ ...trpc.pedidos.atrasados.queryOptions(), refetchInterval: 60_000 })
  const pedidos = useQuery({
    ...trpc.pedidos.listar.queryOptions({ status: 'EM_PRODUCAO', limite: 200 }),
    refetchInterval: 60_000,
  })

  const postos = agora.data ?? []
  const produzindo = postos.filter((p) => p.fichaAtual && p.fichaAtual.estado !== 'PAUSADA').length
  const pausados = postos.filter((p) => p.fichaAtual?.estado === 'PAUSADA').length
  const parados = postos.length - produzindo - pausados
  const estouradas = postos.filter((p) => estourou(p.fichaAtual, relogio)).length

  const produtos = (producao.data?.produtos ?? [])
    .filter((p) => p.pecas > 0)
    .sort((a, b) => b.pecas - a.pecas)
  const pecasHoje = produtos.reduce((n, p) => n + p.pecas, 0)
  const fichasHoje = produtos.reduce((n, p) => n + p.fichasConcluidas, 0)

  // Próximas entregas: em produção e ainda no prazo (os vencidos já estão em "Atrasados").
  const proximas = (pedidos.data ?? []).filter((p) => !prazoVencido(p.prazo))

  // Por linha: quantos postos estão produzindo agora.
  const porLinha = new Map<string, { produzindo: number; total: number }>()
  for (const p of postos) {
    const l = porLinha.get(p.linha ?? 'Outros') ?? { produzindo: 0, total: 0 }
    l.total += 1
    if (p.fichaAtual && p.fichaAtual.estado !== 'PAUSADA') l.produzindo += 1
    porLinha.set(p.linha ?? 'Outros', l)
  }

  const semConexao = agora.isError || producao.isError || atrasados.isError || pedidos.isError
  const atualizadoEm = agora.dataUpdatedAt
    ? new Date(agora.dataUpdatedAt).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col gap-3 p-4 pb-8">
      <div className="flex items-center gap-3">
        <MarcaOP className="size-8 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-xl leading-tight font-bold">Fábrica agora</h1>
          {atualizadoEm && (
            <p className="text-xs text-fg-subtle tabular">atualizado às {atualizadoEm}</p>
          )}
        </div>
        {/* Trocar de área: as telas completas da gestão e o painel da TV (em outra aba). */}
        <nav aria-label="Outras telas" className="ml-auto flex shrink-0 gap-1.5">
          <Link to="/gestao" className={CLASSE_ATALHO}>
            <LayoutDashboard className="size-4" aria-hidden />
            Gestão
          </Link>
          <a href="/painel" target="_blank" rel="noopener" className={CLASSE_ATALHO}>
            <MonitorPlay className="size-4" aria-hidden />
            Painel
          </a>
        </nav>
      </div>
      <ConviteInstalar contexto="diretoria" />
      {semConexao && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-xl bg-amarelo/15 px-3 py-2 text-sm font-medium text-amarelo"
        >
          <CloudOff className="size-4 shrink-0" aria-hidden />
          Sem conexão. Mostrando o último dado recebido.
        </p>
      )}

      {/* Hoje: o número que o diretor quer ver primeiro. */}
      <section aria-label="Produção de hoje" className="card p-4">
        {producao.data ? (
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold tracking-widest text-fg-muted uppercase">
                Peças prontas hoje
              </p>
              <p className="text-5xl leading-none font-bold tabular">
                {pecasHoje.toLocaleString('pt-BR')}
              </p>
            </div>
            <p className="pb-1 text-right text-sm text-fg-muted tabular">
              {plural(fichasHoje, 'ficha fechada', 'fichas fechadas')}
              <br />
              {plural(produtos.length, 'produto', 'produtos')}
            </p>
          </div>
        ) : (
          <EsqueletoLinhas linhas={2} />
        )}
      </section>

      {/* Postos agora, numa barra só. */}
      <section aria-label="Postos agora" className="card p-4">
        {agora.data ? (
          <>
            <div className="mb-3 grid grid-cols-3 gap-2 text-center">
              <Contagem valor={produzindo} rotulo="Produzindo" cor="text-verde" />
              <Contagem valor={pausados} rotulo="Pausados" cor="text-amarelo" />
              <Contagem valor={parados} rotulo="Parados" cor="text-fg-muted" />
            </div>
            <div aria-hidden className="flex h-2.5 overflow-hidden rounded-full bg-elevated">
              <span
                className="bg-verde"
                style={{ width: `${(produzindo / Math.max(1, postos.length)) * 100}%` }}
              />
              <span
                className="bg-amarelo"
                style={{ width: `${(pausados / Math.max(1, postos.length)) * 100}%` }}
              />
            </div>
            <ul className="mt-3 flex flex-col gap-1.5">
              {[...porLinha.entries()].map(([linha, l]) => (
                <li key={linha} className="flex items-center justify-between text-sm">
                  <span className="text-fg-muted">{linha}</span>
                  <span className="tabular">
                    <span className="font-semibold">{l.produzindo}</span>
                    <span className="text-fg-subtle"> de {l.total} produzindo</span>
                  </span>
                </li>
              ))}
            </ul>
            {estouradas > 0 && (
              <p className="mt-3 flex items-center gap-2 rounded-lg bg-vermelho/10 px-3 py-2 text-sm font-medium text-vermelho">
                <TriangleAlert className="size-4 shrink-0" aria-hidden />
                {plural(estouradas, 'ficha passou', 'fichas passaram')} do tempo previsto
              </p>
            )}
          </>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <EsqueletoCartoes quantidade={3} />
          </div>
        )}
      </section>

      <Secao
        titulo="Pedidos atrasados"
        alerta={!!atrasados.data?.length}
        total={atrasados.data?.length}
        carregando={atrasados.isPending}
        vazio="Nenhum pedido atrasado."
        itens={(atrasados.data ?? []).map((p) => (
          <LinhaPedido
            key={p.id}
            numero={p.numero}
            produtos={p.itens.map((i) => i.produto.nome).join(', ')}
            detalhe={
              <span className="font-semibold text-vermelho">
                {plural(p.diasAtraso, 'dia', 'dias')} de atraso
              </span>
            }
            percentual={p.percentual}
          />
        ))}
      />

      <Secao
        titulo="Próximas entregas"
        total={proximas.length}
        carregando={pedidos.isPending}
        vazio="Nenhum pedido em produção no prazo."
        itens={proximas.map((p) => (
          <LinhaPedido
            key={p.id}
            numero={p.numero}
            produtos={p.itens.map((i) => i.produto.nome).join(', ')}
            detalhe={p.prazo ? `entrega ${quando(p.prazo)}` : 'sem prazo'}
            percentual={p.progresso.percentual}
          />
        ))}
      />

      <Secao
        titulo="Mais produzidos hoje"
        total={produtos.length}
        carregando={producao.isPending}
        vazio="Nada pronto ainda hoje."
        itens={produtos.map((p) => (
          <li key={p.produto.id} className="flex items-center gap-3">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{p.produto.nome}</span>
              <span className="block text-xs text-fg-subtle">
                {plural(p.fichasConcluidas, 'ficha fechada', 'fichas fechadas')} ·{' '}
                {p.fichasEmProducao} em produção
              </span>
            </span>
            <span className="shrink-0 text-xl font-bold tabular">
              {p.pecas.toLocaleString('pt-BR')}
            </span>
          </li>
        ))}
      />
      <div className="mt-2 flex items-center justify-center gap-2">
        <Link
          to="/minha-senha"
          search={{ voltar: '/diretoria' }}
          className="rounded-lg px-2 py-1 text-xs text-fg-subtle transition-colors hover:text-fg"
        >
          Trocar minha senha
        </Link>
        <span aria-hidden className="text-fg-subtle">
          ·
        </span>
        <VersaoApp />
      </div>
    </main>
  )
}

function Contagem({ valor, rotulo, cor }: { valor: number; rotulo: string; cor: string }) {
  return (
    <div>
      <p className={cn('text-3xl leading-none font-bold tabular', cor)}>{valor}</p>
      <p className="mt-1 text-xs text-fg-muted">{rotulo}</p>
    </div>
  )
}

/** Seção com os primeiros itens à vista e o resto atrás de "Ver todos". */
function Secao({
  titulo,
  total,
  itens,
  vazio,
  carregando,
  alerta = false,
}: {
  titulo: string
  total: number | undefined
  itens: ReactNode[]
  vazio: string
  carregando: boolean
  alerta?: boolean
}) {
  const [aberta, setAberta] = useState(false)
  const visiveis = aberta ? itens : itens.slice(0, RESUMO)
  const resto = itens.length - RESUMO
  return (
    <section aria-label={titulo} className="card p-4">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold tracking-widest text-fg-muted uppercase">
        {alerta && <TriangleAlert className="size-4 text-vermelho" aria-hidden />}
        {titulo}
        {total != null && total > 0 && (
          <span
            className={cn(
              'rounded-full px-2 tabular',
              alerta ? 'bg-vermelho/20 text-vermelho' : 'bg-elevated text-fg-muted',
            )}
          >
            {total}
          </span>
        )}
      </h2>
      {carregando ? (
        <EsqueletoLinhas linhas={3} />
      ) : itens.length === 0 ? (
        <p className="text-sm text-fg-subtle">{vazio}</p>
      ) : (
        <>
          <ul className="flex flex-col gap-3">{visiveis}</ul>
          {resto > 0 && (
            <button
              type="button"
              onClick={() => setAberta((a) => !a)}
              aria-expanded={aberta}
              className="mt-3 flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-elevated/60 text-sm font-semibold text-fg-muted hover:text-fg"
            >
              {aberta ? 'Mostrar menos' : `Ver todos (mais ${resto})`}
              <ChevronDown
                className={cn('size-4 transition-transform', aberta && 'rotate-180')}
                aria-hidden
              />
            </button>
          )}
        </>
      )}
    </section>
  )
}

function LinhaPedido({
  numero,
  produtos,
  detalhe,
  percentual,
}: {
  numero: number
  produtos: string
  detalhe: ReactNode
  percentual: number
}) {
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-3">
        <span className="min-w-10 shrink-0 font-semibold tabular">#{numero}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">{produtos}</span>
        <span className="shrink-0 text-xs tabular">{detalhe}</span>
      </div>
      <div className="flex items-center gap-3">
        <div
          role="progressbar"
          aria-label={`Pedido ${numero}: ${percentual}% das etapas concluídas`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percentual}
          className="h-2 flex-1 overflow-hidden rounded-full bg-elevated"
        >
          <div className="h-full rounded-full bg-brand" style={{ width: `${percentual}%` }} />
        </div>
        <span className="w-10 shrink-0 text-right text-sm font-semibold tabular">
          {percentual}%
        </span>
      </div>
    </li>
  )
}
